import { VOCABULARY } from '@vello/country-vocabularies';
import { useMemo } from 'react';
import { useT } from './provider';

type Vars = Readonly<Record<string, string | number>>;

/** The part of an `ApiError` a screen needs to explain a refusal. */
export interface FailureLike {
  readonly code: string;
  readonly reasonCode?: string | undefined;
  readonly params?: Vars | undefined;
  /** HTTP status; a bare 403 / 404 / 409 / 503 reads as the surface's wording of that class. */
  readonly status?: number | undefined;
  /** Older servers sent the reason in `detail` (read only when there is no `reasonCode`). */
  readonly detail?: string | undefined;
}

/**
 * D577 (ADR-0105): the ONE way a screen shows a stored code — a status, reason, event, method, role, permission or audit
 * action. `v('ride.status', code)` gives the human label in the console's language; `reason(code)` the sentence for a refusal
 * `reasonCode` from any route; `fail(error)` the words for a failed call (its domain reason when it has one, else the
 * transport class), without the closing full stop so it fits inside a screen's own sentence ("Could not save ({code})").
 * Never render a code, never humanize one: an unknown code reads as the translated "uncatalogued value" (or the generic
 * refusal), and the catalog's gate keeps that from being the normal path.
 */
export function useVocab(): {
  readonly v: (id: string, code: string | null | undefined, vars?: Vars) => string;
  readonly reason: (code: string | null | undefined, vars?: Vars) => string;
  readonly fail: (error: FailureLike) => string;
  /** The catalogued sentence for a refusal's `reasonCode` (with its `params`), or `undefined` when the catalog does not know it. */
  readonly explain: (error: FailureLike) => string | undefined;
  /**
   * D593 (ADR-0105 amendment): the words of a refusal on one surface — `refusal('refusal.admin.cash_handover', error)`. The
   * surface's own guidance for the reason code, else the system-wide sentence of the code, else the surface's wording of the
   * HTTP class; `undefined` when none applies (the screen shows its generic line). A screen never maps a reason code to a
   * message id of its own.
   */
  readonly refusal: (surface: string, error: FailureLike, vars?: Vars) => string | undefined;
  readonly has: (id: string, code: string | null | undefined) => boolean;
  /** Whether some `problem.*` vocabulary carries a sentence for this reason code. */
  readonly hasReason: (code: string | null | undefined) => boolean;
  /** The translated "uncatalogued value" text — what a screen shows where a source other than the catalog has no label for a code. */
  readonly uncatalogued: string;
  /** The name of a content-language tag (`pt`, `en-US`) in the console's language; an unreadable tag reads as the uncatalogued value. */
  readonly language: (tag: string | null | undefined) => string;
} {
  const { lang } = useT();
  return useMemo(
    () => ({
      v: (id: string, code: string | null | undefined, vars?: Vars) => VOCABULARY.label(id, code, lang, vars),
      reason: (code: string | null | undefined, vars?: Vars) => VOCABULARY.reason(code, lang, vars),
      fail: (error: FailureLike) => {
        const text =
          error.reasonCode !== undefined && VOCABULARY.hasReason(error.reasonCode)
            ? VOCABULARY.reason(error.reasonCode, lang, error.params)
            : VOCABULARY.label('api.error_code', error.code, lang);
        return text.replace(/[.\s]+$/, '');
      },
      explain: (error: FailureLike) =>
        error.reasonCode !== undefined && VOCABULARY.hasReason(error.reasonCode) ? VOCABULARY.reason(error.reasonCode, lang, error.params) : undefined,
      refusal: (surface: string, error: FailureLike, vars?: Vars) => VOCABULARY.refusal(surface, error, lang, vars),
      has: VOCABULARY.has,
      uncatalogued: VOCABULARY.label('', 'x', lang),
      hasReason: VOCABULARY.hasReason,
      language: (tag: string | null | undefined) => {
        if (tag === null || tag === undefined || tag === '') return VOCABULARY.label('common.language', tag, lang);
        try {
          const name = new Intl.DisplayNames([lang], { type: 'language' }).of(tag.replace(/_/g, '-'));
          if (name !== undefined && name !== tag.replace(/_/g, '-')) return name;
        } catch {
          // an unreadable tag falls through to the uncatalogued value
        }
        return VOCABULARY.label('common.language', tag, lang);
      },
    }),
    [lang],
  );
}
